import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import api from '../../services/api';
import './DataManager.css';

function DataManager() {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [symbolFilter, setSymbolFilter] = useState('');
  const [stepFilter, setStepFilter] = useState('');
  const [symbolsList, setSymbolsList] = useState([]);
  const [stepsList, setStepsList] = useState([]);
  const [symbolSearch, setSymbolSearch] = useState('');
  const [stepSearch, setStepSearch] = useState('');
  const [showSymbolDropdown, setShowSymbolDropdown] = useState(false);
  const [showStepDropdown, setShowStepDropdown] = useState(false);
  const [targetId, setTargetId] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    id: '',
    Date: '',
    Symbol: '',
    Type: 'buy',
    MarketType: 'Bourse',
    Quantity: 0,
    Price: 0,
    Step: '',
    BlockedAmount: 0,
    Note: ''
  });
  const [bulkDeleteId, setBulkDeleteId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [rollbackResult, setRollbackResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [transactions, symbolFilter, stepFilter, fromDate, toDate]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/transactions');
      setTransactions(data);
      setFilteredTransactions(data);

      const uniqueSymbols = [...new Set(data.map(t => t.Symbol))];
      const uniqueSteps = [...new Set(data.map(t => t.Step))];
      setSymbolsList(uniqueSymbols);
      setStepsList(uniqueSteps);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      showMessage('error', 'خطا در دریافت اطلاعات');
    }
    setLoading(false);
  };

  const applyFilters = () => {
    let filtered = [...transactions];

    if (symbolFilter) {
      filtered = filtered.filter(t => t.Symbol === symbolFilter);
    }

    if (stepFilter) {
      filtered = filtered.filter(t => t.Step === stepFilter);
    }

    if (fromDate) {
      filtered = filtered.filter(t => t.Date >= fromDate);
    }

    if (toDate) {
      filtered = filtered.filter(t => t.Date <= toDate);
    }

    setFilteredTransactions(filtered);
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleSymbolSelect = (symbol) => {
    setSymbolFilter(symbol);
    setSymbolSearch('');
    setShowSymbolDropdown(false);
  };

  const handleStepSelect = (step) => {
    setStepFilter(step);
    setStepSearch('');
    setShowStepDropdown(false);
  };

  const clearSymbolFilter = () => {
    setSymbolFilter('');
    setSymbolSearch('');
  };

  const clearStepFilter = () => {
    setStepFilter('');
    setStepSearch('');
  };

  const getFilteredSymbols = () => {
    if (!symbolSearch) return symbolsList;
    return symbolsList.filter(sym =>
      sym.toLowerCase().includes(symbolSearch.toLowerCase())
    );
  };

  const getFilteredSteps = () => {
    if (!stepSearch) return stepsList;
    return stepsList.filter(step =>
      step.toLowerCase().includes(stepSearch.toLowerCase())
    );
  };

  const handleSearchId = () => {
    if (!targetId) {
      showMessage('warning', 'لطفاً ID ردیف را وارد کنید');
      return;
    }

    const row = filteredTransactions.find(t => t.id === parseInt(targetId));
    if (row) {
      setEditForm({
        id: row.id,
        Date: row.Date,
        Symbol: row.Symbol,
        Type: row.Type,
        MarketType: row.MarketType,
        Quantity: row.Quantity,
        Price: row.Price,
        Step: row.Step || '',
        BlockedAmount: row.BlockedAmount || 0,
        Note: row.Note || ''
      });
      setEditMode(true);
    } else {
      showMessage('error', `ردیف با ID ${targetId} یافت نشد`);
      setEditMode(false);
    }
  };

  const handleEditChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      await api.put(`/api/transactions/${editForm.id}`, {
        Date: editForm.Date,
        Symbol: editForm.Symbol,
        Type: editForm.Type,
        MarketType: editForm.MarketType,
        Quantity: editForm.Quantity,
        Price: editForm.Price,
        Step: editForm.Step,
        BlockedAmount: editForm.BlockedAmount,
        Note: editForm.Note
      });
      showMessage('success', `تراکنش شماره ${editForm.id} ویرایش شد.`);
      fetchTransactions();
      setEditMode(false);
      setTargetId('');
    } catch (error) {
      console.error('Error updating transaction:', error);
      showMessage('error', 'خطا در ارتباط با سرور');
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!targetId) {
      showMessage('warning', 'لطفاً ID ردیف را وارد کنید');
      return;
    }

    if (!window.confirm(`آیا از حذف تراکنش شماره ${targetId} مطمئن هستید؟`)) {
      return;
    }

    setLoading(true);
    try {
      await api.delete(`/api/transactions/${targetId}`);
      showMessage('success', `تراکنش شماره ${targetId} حذف شد.`);
      fetchTransactions();
      setEditMode(false);
      setTargetId('');
    } catch (error) {
      console.error('Error deleting transaction:', error);
      showMessage('error', 'خطا در ارتباط با سرور');
    }
    setLoading(false);
  };

  const handleBulkDeleteById = async () => {
    const targetId = parseInt(bulkDeleteId, 10);
    if (isNaN(targetId) || targetId <= 0) {
      showMessage('warning', 'لطفاً یک ID معتبر وارد کنید');
      return;
    }

    if (!window.confirm(`آیا مطمئن هستید که می‌خواهید تراکنش با ID ${targetId} و تمام تراکنش‌های بعد از آن را حذف کنید؟\nاین عمل غیرقابل بازگشت است!`)) {
      return;
    }

    setRollbackLoading(true);
    setRollbackResult(null);

    try {
      const result = await api.post('/api/transactions/rollback-by-id', { targetId });

      setRollbackResult(result);
      showMessage('success', `با موفقیت ${result.deletedCount} تراکنش با ID ${targetId} و بالاتر حذف شد.`);

      // Refresh transactions list
      fetchTransactions();

      // Clear the ID field
      setBulkDeleteId('');

    } catch (error) {
      console.error('Error deleting by ID:', error);
      showMessage('error', 'خطا در ارتباط با سرور');
    }

    setRollbackLoading(false);
  };

  const getTypePersian = (type) => {
    const types = {
      buy: 'خرید',
      sell: 'فروش',
      short_sell: 'فروش فزاینده',
      hami_profit: 'سود حامی',
      margin: 'شارژ مارجین',
      bonus: 'جایزه'
    };
    return types[type] || type;
  };

  const exportToExcel = () => {
    if (filteredTransactions.length === 0) {
      alert('داده‌ای برای خروجی وجود ندارد');
      return;
    }

    const excelData = filteredTransactions.map(tx => ({
      'ID': tx.id,
      'تاریخ': tx.Date,
      'نماد': tx.Symbol,
      'نوع': getTypePersian(tx.Type),
      'بازار': tx.MarketType,
      'تعداد': tx.Quantity,
      'قیمت': tx.Price,
      'پله': tx.Step || '',
      'وجه تضمین': tx.BlockedAmount || 0,
      'یادداشت': tx.Note || ''
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'تراکنش‌ها');
    XLSX.writeFile(workbook, `تراکنش‌ها_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const mapExcelRowToTransaction = (row) => {
    const persianTypeMap = {
      'خرید': 'buy',
      'فروش': 'sell',
      'فروش فزاینده': 'short_sell',
      'سود حامی': 'hami_profit',
      'جایزه': 'bonus',
      'شارژ مارجین': 'margin'
    };

    const normalizePersianText = (text) => {
      if (!text || typeof text !== 'string') return String(text || '');
      const mapping = {
        'ي': 'ی', 'ك': 'ک',
        '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4',
        '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9'
      };
      let result = text;
      for (const [k, v] of Object.entries(mapping)) {
        result = result.replace(new RegExp(k, 'g'), v);
      }
      return result.trim();
    };

    return {
      Date: row['تاریخ'] || '',
      Symbol: normalizePersianText(String(row['نماد'] || '').trim()),
      Type: persianTypeMap[row['نوع']] || row['نوع'] || '',
      MarketType: row['بازار'] || '',
      Quantity: parseFloat(row['تعداد']) || 0,
      Price: parseFloat(row['قیمت']) || 0,
      Step: row['پله'] || '',
      BlockedAmount: parseFloat(row['وجه تضمین']) || 0,
      Note: row['یادداشت'] || ''
    };
  };

  const handleImportFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportFile(file);
    setImportResult(null);
    setImportPreview([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);

        if (jsonData.length === 0) {
          showMessage('error', 'فایل اکسل خالی است');
          setImportFile(null);
          return;
        }

        const firstRow = jsonData[0];
        const requiredColumns = ['تاریخ', 'نماد', 'نوع', 'بازار'];
        const missingColumns = requiredColumns.filter(col => !(col in firstRow));
        if (missingColumns.length > 0) {
          showMessage('error', `ستون‌های الزامی یافت نشد: ${missingColumns.join('، ')}`);
          setImportFile(null);
          setImportPreview([]);
          return;
        }

        const mapped = jsonData.map(mapExcelRowToTransaction);
        setImportPreview(mapped.slice(0, 5));
      } catch (error) {
        console.error('Error parsing Excel:', error);
        showMessage('error', 'خطا در خواندن فایل اکسل');
        setImportFile(null);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImportSubmit = async () => {
    if (!importFile || importPreview.length === 0) {
      showMessage('warning', 'لطفاً ابتدا فایل اکسل را انتخاب کنید');
      return;
    }

    if (!window.confirm('آیا از وارد کردن داده‌های اکسل به دیتابیس جدید مطمئن هستید؟')) {
      return;
    }

    setImportLoading(true);
    try {
      const data = new Uint8Array(await importFile.arrayBuffer());
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(firstSheet);
      const mapped = jsonData.map(mapExcelRowToTransaction);

      const result = await api.post('/api/transactions/migrate', mapped);
      setImportResult(result);
      if (result.success) {
        showMessage('success', `${result.insertedCount} تراکنش از ${result.totalRows} ردیف با موفقیت وارد شد.`);
        fetchTransactions();
        setImportFile(null);
        setImportPreview([]);
      } else {
        showMessage('error', 'خطا در وارد کردن داده‌ها');
      }
    } catch (error) {
      console.error('Import error:', error);
      showMessage('error', 'خطا در ارتباط با سرور');
    }
    setImportLoading(false);
  };

  return (
    <div className="data-manager">
      <h1>🛠️ مدیریت تراکنش‌ها</h1>

      {message.text && (
        <div className={`message-box ${message.type}`}>
          {message.text}
        </div>
      )}

      {loading && <div className="loading-overlay">در حال پردازش...</div>}

      <div className="import-section">
        <h3>📥 ورود داده‌ها از داشبورد قبلی (اکسل)</h3>
        <div className="import-info">
          فایل خروجی اکسل داشبورد قبلی را انتخاب کنید. ستون‌های مورد انتظار: تاریخ، نماد، نوع، بازار، تعداد، قیمت، پله، وجه تضمین، یادداشت.
        </div>
        <div className="import-file-row">
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleImportFileSelect}
            disabled={importLoading}
          />
          <button
            onClick={handleImportSubmit}
            className="import-btn"
            disabled={importLoading || !importFile || importPreview.length === 0}
          >
            {importLoading ? 'در حال وارد کردن...' : '✅ وارد کردن داده‌ها'}
          </button>
        </div>

        {importPreview.length > 0 && (
          <div className="preview-section">
            <h4>پیش‌نمایش ۵ ردیف اول ({importPreview.length} ردیف نمونه):</h4>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>تاریخ</th>
                    <th>نماد</th>
                    <th>نوع</th>
                    <th>بازار</th>
                    <th>تعداد</th>
                    <th>قیمت</th>
                    <th>پله</th>
                  </tr>
                </thead>
                <tbody>
                  {importPreview.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.Date}</td>
                      <td>{row.Symbol}</td>
                      <td>{getTypePersian(row.Type)}</td>
                      <td>{row.MarketType}</td>
                      <td>{row.Quantity.toLocaleString()}</td>
                      <td>{row.Price.toLocaleString()}</td>
                      <td>{row.Step || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {importResult && (
          <div className={`result-box ${importResult.success ? 'success' : 'error'}`}>
            {importResult.success
              ? `✅ ${importResult.insertedCount} تراکنش از ${importResult.totalRows} ردیف با موفقیت وارد شد.`
              : `❌ خطا در وارد کردن داده‌ها`
            }
            {importResult.errors && importResult.errors.length > 0 && (
              <div style={{ marginTop: '10px', fontSize: '0.85rem' }}>
                <strong>خطاها ({importResult.errors.length}):</strong>
                <ul>
                  {importResult.errors.slice(0, 5).map((err, idx) => (
                    <li key={idx}>ردیف {err.row}: {err.reason}</li>
                  ))}
                  {importResult.errors.length > 5 && <li>... و {importResult.errors.length - 5} خطای دیگر</li>}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="edit-section">
        <h3>✏️ ویرایش / حذف</h3>

        <div className="search-id">
          <label>ID ردیف:</label>
          <div className="search-row">
            <input
              type="number"
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              placeholder="مثال: 5"
              min="0"
            />
            <button onClick={handleSearchId} className="search-btn">جستجو</button>
          </div>
        </div>

        {editMode && (
          <div className="edit-form">
            <h4>ویرایش تراکنش #{editForm.id}</h4>

            <div className="form-row">
              <div className="form-group">
                <label>تاریخ:</label>
                <input type="date" value={editForm.Date} onChange={(e) => handleEditChange('Date', e.target.value)} />
              </div>
              <div className="form-group">
                <label>نماد:</label>
                <input type="text" value={editForm.Symbol} onChange={(e) => handleEditChange('Symbol', e.target.value)} />
              </div>
              <div className="form-group">
                <label>نوع:</label>
                <select value={editForm.Type} onChange={(e) => handleEditChange('Type', e.target.value)}>
                  <option value="buy">خرید</option>
                  <option value="sell">فروش</option>
                  <option value="margin">شارژ مارجین</option>
                  <option value="bonus">جایزه</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>بازار:</label>
                <select value={editForm.MarketType} onChange={(e) => handleEditChange('MarketType', e.target.value)}>
                  <option value="Bourse">Bourse</option>
                  <option value="FaraBourse">FaraBourse</option>
                  <option value="Gold">Gold</option>
                  <option value="Future">Future</option>
                  <option value="Option">Option</option>
                  <option value="Hami">Hami</option>
                </select>
              </div>
              <div className="form-group">
                <label>تعداد:</label>
                <input type="number" value={editForm.Quantity} onChange={(e) => handleEditChange('Quantity', parseFloat(e.target.value))} step="1" />
              </div>
              <div className="form-group">
                <label>قیمت:</label>
                <input type="number" value={editForm.Price} onChange={(e) => handleEditChange('Price', parseFloat(e.target.value))} step="100" />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>پله:</label>
                <input type="text" value={editForm.Step} onChange={(e) => handleEditChange('Step', e.target.value)} />
              </div>
              <div className="form-group">
                <label>وجه تضمین:</label>
                <input type="number" value={editForm.BlockedAmount} onChange={(e) => handleEditChange('BlockedAmount', parseFloat(e.target.value))} step="100000" />
              </div>
            </div>

            <div className="form-group full-width">
              <label>یادداشت:</label>
              <textarea value={editForm.Note} onChange={(e) => handleEditChange('Note', e.target.value)} rows="3"></textarea>
            </div>

            <div className="form-actions">
              <button onClick={handleUpdate} className="save-btn" disabled={loading}>
                💾 ذخیره تغییرات
              </button>
              <button onClick={handleDelete} className="delete-btn" disabled={loading}>
                🗑️ حذف ردیف
              </button>
              <button onClick={() => { setEditMode(false); setTargetId(''); }} className="cancel-btn">
                انصراف
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="rollback-section">
        <h3>🗑️ حذف گروهی بر اساس ID</h3>
        <div className="info-box warning">
          ⚠️ توجه: تمام تراکنش‌ها با ID بزرگتر یا مساوی مقدار وارد شده حذف می‌شوند. این عمل غیرقابل بازگشت است!
        </div>

        <div className="rollback-form">
          <div className="form-row">
            <div className="form-group">
              <label>ID هدف (حذف تراکنش‌ها با ID ≥ این مقدار):</label>
              <input
                type="number"
                value={bulkDeleteId}
                onChange={(e) => setBulkDeleteId(e.target.value)}
                disabled={rollbackLoading}
                placeholder="مثال: 500"
                min="1"
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              onClick={handleBulkDeleteById}
              className="rollback-btn"
              disabled={rollbackLoading || !bulkDeleteId}
            >
              {rollbackLoading ? 'در حال حذف...' : '🗑️ حذف تراکنش‌ها از این ID به بعد'}
            </button>
          </div>

          {rollbackResult && (
            <div className="result-box success">
              ✅ با موفقیت {rollbackResult.deletedCount} تراکنش با ID ≥ {bulkDeleteId} حذف شد.
            </div>
          )}
        </div>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <label>فیلتر نماد:</label>
          <div className="searchable-dropdown">
            <div className="dropdown-input">
              <input
                type="text"
                placeholder="جستجوی نماد..."
                value={symbolSearch}
                onChange={(e) => {
                  setSymbolSearch(e.target.value);
                  setShowSymbolDropdown(true);
                }}
                onFocus={() => setShowSymbolDropdown(true)}
              />
              {symbolFilter && (
                <span className="selected-badge">
                  {symbolFilter}
                  <button className="clear-badge" onClick={clearSymbolFilter}>×</button>
                </span>
              )}
              <button className="dropdown-arrow" onClick={() => setShowSymbolDropdown(!showSymbolDropdown)}>
                ▼
              </button>
            </div>
            {showSymbolDropdown && (
              <div className="dropdown-list">
                <div className="dropdown-search">
                  <input
                    type="text"
                    placeholder="جستجو..."
                    value={symbolSearch}
                    onChange={(e) => setSymbolSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="dropdown-options">
                  <div className="dropdown-option" onClick={() => handleSymbolSelect('')}>
                    <em>همه نمادها</em>
                  </div>
                  {getFilteredSymbols().map(sym => (
                    <div
                      key={sym}
                      className={`dropdown-option ${symbolFilter === sym ? 'active' : ''}`}
                      onClick={() => handleSymbolSelect(sym)}
                    >
                      {sym}
                    </div>
                  ))}
                  {getFilteredSymbols().length === 0 && (
                    <div className="dropdown-option disabled">موردی یافت نشد</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="filter-group">
          <label>فیلتر پله:</label>
          <div className="searchable-dropdown">
            <div className="dropdown-input">
              <input
                type="text"
                placeholder="جستجوی پله..."
                value={stepSearch}
                onChange={(e) => {
                  setStepSearch(e.target.value);
                  setShowStepDropdown(true);
                }}
                onFocus={() => setShowStepDropdown(true)}
              />
              {stepFilter && (
                <span className="selected-badge">
                  {stepFilter}
                  <button className="clear-badge" onClick={clearStepFilter}>×</button>
                </span>
              )}
              <button className="dropdown-arrow" onClick={() => setShowStepDropdown(!showStepDropdown)}>
                ▼
              </button>
            </div>
            {showStepDropdown && (
              <div className="dropdown-list">
                <div className="dropdown-search">
                  <input
                    type="text"
                    placeholder="جستجو..."
                    value={stepSearch}
                    onChange={(e) => setStepSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="dropdown-options">
                  <div className="dropdown-option" onClick={() => handleStepSelect('')}>
                    <em>همه پله‌ها</em>
                  </div>
                  {getFilteredSteps().map(step => (
                    <div
                      key={step}
                      className={`dropdown-option ${stepFilter === step ? 'active' : ''}`}
                      onClick={() => handleStepSelect(step)}
                    >
                      {step}
                    </div>
                  ))}
                  {getFilteredSteps().length === 0 && (
                    <div className="dropdown-option disabled">موردی یافت نشد</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="filter-group date-filter">
          <label>از تاریخ:</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>

        <div className="filter-group date-filter">
          <label>تا تاریخ:</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>

        <div className="filter-group export-group">
          <label>&nbsp;</label>
          <button className="btn-export" onClick={exportToExcel}>
            📥 دانلود اکسل
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>تاریخ</th>
              <th>نماد</th>
              <th>نوع</th>
              <th>بازار</th>
              <th>تعداد</th>
              <th>قیمت</th>
              <th>پله</th>
              <th>وجه تضمین</th>
              <th>یادداشت</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.map(tx => (
              <tr key={tx.id}>
                <td>{tx.id}</td>
                <td>{tx.Date}</td>
                <td>{tx.Symbol}</td>
                <td>{getTypePersian(tx.Type)}</td>
                <td>{tx.MarketType}</td>
                <td>{tx.Quantity.toLocaleString()}</td>
                <td>{tx.Price.toLocaleString()}</td>
                <td>{tx.Step || '-'}</td>
                <td>{(tx.BlockedAmount || 0).toLocaleString()}</td>
                <td className="note-cell">{tx.Note || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredTransactions.length === 0 && (
          <div className="no-data">هیچ داده‌ای یافت نشد</div>
        )}
      </div>
    </div>
  );
}

export default DataManager;